import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Product, apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders, getImpersonationIdentityKey, makeApiCall } from '../../utils/apiConfig';
import {
  applyCatalogBarcodesToVariants,
  completeFamilyVariantRows,
  variantDisplayLabel,
  type NormalizedProductVariant,
} from '../../utils/productVariants';
import {
  CameraWithPricing,
  variantOptionIsCurrent,
  linkedVariantKindLabel,
  toAbsoluteImageUrl,
  toListingPricingData,
  useProductPriceFormatters,
} from '../../utils/productDetailHelpers';
import { linkedCatalogListedPrice } from '../../utils/mapPricing';
import { NeutralNote } from './NeutralNote';
import { tokens } from '../../theme/tokens';

const KIND_TONE: Record<string, { bg: string; color: string }> = {
  kit: { bg: tokens.signal.info.tint, color: tokens.signal.info.ink },
  // Violet has no theme token; it marks one entry in the kit/bundle/configuration/style
  // badge set and stays literal rather than reusing another tone's meaning.
  // eslint-disable-next-line no-restricted-syntax
  bundle: { bg: '#f5f3ff', color: '#6d28d9' },
  configuration: { bg: tokens.surface.tint, color: 'text.secondary' },
  style: { bg: tokens.surface.tint, color: tokens.ink.muted },
};

interface ProductVariantsTableProps {
  product: CameraWithPricing;
  variants: NormalizedProductVariant[];
  linkedVariants: Product[];
  linkedVariantsLoading: boolean;
  variantSwitchLoadingSku: string | null;
  isStoreScoped: boolean;
  onVariantClick: (variant: NormalizedProductVariant) => void;
  onLinkedProductClick: (product: Product) => void;
}

/**
 * The embedded `variants` array holds identifiers only (sku/upc/ean/mpn) —
 * no price, no sync state of its own, since sync membership is store-level
 * keyed by product id (confirmed in the design chat's final correction).
 * "Linked Variants" below is a distinct, real feature: separate catalog
 * product records (with their own price/sync), which the design never
 * covered but which real data genuinely has.
 */
export function ProductVariantsTable({
  product,
  variants,
  linkedVariants,
  linkedVariantsLoading,
  variantSwitchLoadingSku,
  isStoreScoped,
  onVariantClick,
  onLinkedProductClick,
}: ProductVariantsTableProps) {
  const { formatMoneyForProduct } = useProductPriceFormatters();
  const { currentUser } = useAuth();
  const identityKey = `${currentUser?.uid || 'anonymous'}|${getImpersonationIdentityKey()}`;
  const linkedIds = linkedVariants.map((row) => row.id).filter(Boolean);

  const storePricesQuery = useQuery({
    queryKey: ['linked-variant-store-prices', identityKey, linkedIds.join(',')],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(
        `/store/pricing/breakdown?productIds=${encodeURIComponent(linkedIds.join(','))}`,
        { headers },
      );
      if (!response.ok) return {} as Record<string, number>;
      const data = ((await response.json()).data || null) as {
        items?: Array<{ productId: string; found: boolean; effectivePrice?: number }>;
      } | null;
      const prices: Record<string, number> = {};
      for (const item of data?.items || []) {
        if (item.found && item.effectivePrice != null && item.effectivePrice > 0) {
          prices[item.productId] = item.effectivePrice;
        }
      }
      return prices;
    },
    enabled: isStoreScoped && linkedIds.length > 0,
    staleTime: 30_000,
  });

  const pricingLookupItems = useMemo(
    () =>
      linkedVariants
        .filter((row) => !!(row.model || row.sku))
        .map((row) => ({
          model: row.model || row.sku || '',
          productId: row.id,
          brand: row.brand,
          sku: row.sku,
          mpn: row.mpn,
        })),
    [linkedVariants],
  );

  const pricingQuery = useQuery({
    queryKey: ['linked-variant-map', pricingLookupItems.map((item) => `${item.productId || ''}::${item.model}`)],
    queryFn: () => apiService.getPricingByModels(pricingLookupItems),
    enabled: pricingLookupItems.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const familyVariants = useMemo(
    () => completeFamilyVariantRows(variants, product, linkedVariants),
    [variants, product, linkedVariants],
  );

  const { rows, warning } = useMemo(() => {
    const resolved = applyCatalogBarcodesToVariants(familyVariants, [product, ...linkedVariants]);
    const mpnCounts: Record<string, number> = {};
    resolved.forEach((v) => {
      if (v.mpn) mpnCounts[v.mpn] = (mpnCounts[v.mpn] || 0) + 1;
    });
    const sharedMpns = Object.keys(mpnCounts).filter((k) => mpnCounts[k] > 1);
    // The column is "UPC / EAN": either identifier satisfies the check.
    const missingUpc = resolved.filter((v) => !v.upc && !v.ean).length;

    const issues: string[] = [];
    if (sharedMpns.length) {
      issues.push(
        sharedMpns.length === 1
          ? `MPN ${sharedMpns[0]} is repeated across ${mpnCounts[sharedMpns[0]]} options`
          : `${sharedMpns.length} MPNs are repeated across options`
      );
    }
    if (missingUpc) {
      issues.push(`${missingUpc} option${missingUpc > 1 ? 's have' : ' has'} no UPC or EAN`);
    }

    const builtRows = resolved.map((v) => {
      const isCurrent = variantOptionIsCurrent(product, v);
      const sharedMpn = !!v.mpn && mpnCounts[v.mpn] > 1;
      const label = variantDisplayLabel(v);
      const targetSku = (v.sku || '').trim();
      return {
        variant: v,
        label,
        isCurrent,
        sharedMpn,
        canOpen: !!targetSku && !isCurrent,
        isLoading: !!targetSku && variantSwitchLoadingSku === targetSku,
      };
    });

    return {
      rows: builtRows,
      warning: issues.length
        ? `${issues.join(' · ')}. A shared MPN is how a body's pricing row gets stamped onto a kit.`
        : null,
    };
  }, [familyVariants, product, linkedVariants, variantSwitchLoadingSku]);

  if (familyVariants.length <= 1 && !linkedVariantsLoading && linkedVariants.length <= 1) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {familyVariants.length > 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
              Variants &amp; kits
            </Typography>
            <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{familyVariants.length}</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>Every configuration in this family, including this one</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: { xs: '1fr', sm: '1fr 108px 132px 108px' }, gap: 1.5, pb: 0.75, fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint }}>
              <span>Option</span><span>SKU</span><span>UPC / EAN</span><span>MPN</span>
            </Box>
            {rows.map(({ variant, label, isCurrent, sharedMpn, canOpen, isLoading }, i) => (
              <Box
                key={`${variant.sku || label}-${i}`}
                role={canOpen ? 'button' : undefined}
                tabIndex={canOpen ? 0 : undefined}
                onClick={() => canOpen && !isLoading && onVariantClick(variant)}
                onKeyDown={(e) => {
                  if (canOpen && !isLoading && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onVariantClick(variant);
                  }
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 108px 132px 108px' },
                  gap: { xs: 0.5, sm: 1.5 },
                  alignItems: 'baseline',
                  py: '9px',
                  borderBottom: '1px solid',
                  borderColor: tokens.surface.sunken,
                  bgcolor: isCurrent ? tokens.surface.subtle : 'transparent',
                  cursor: canOpen ? 'pointer' : 'default',
                  '&:hover': canOpen ? { bgcolor: tokens.surface.sunken } : undefined,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {variant.kind && (
                    <Box
                      component="span"
                      sx={{
                        flex: '0 0 auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 19,
                        px: '7px',
                        borderRadius: '5px',
                        bgcolor: KIND_TONE[variant.kind]?.bg ?? tokens.surface.tint,
                        color: KIND_TONE[variant.kind]?.color ?? 'text.secondary',
                        fontSize: '10.5px',
                        fontWeight: 600,
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {variant.kind}
                    </Box>
                  )}
                  <Typography sx={{ minWidth: 0, fontSize: '13px', fontWeight: isCurrent ? 600 : 400, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {label}
                  </Typography>
                  {isCurrent && <Typography sx={{ flex: '0 0 auto', fontSize: '11.5px', color: 'text.secondary' }}>viewing</Typography>}
                  {isLoading && <CircularProgress size={12} sx={{ flex: '0 0 auto' }} />}
                </Box>
                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' }, color: tokens.ink.faint, mr: 0.75 }}>SKU</Box>
                  {variant.sku || '—'}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: variant.upc || variant.ean ? 'text.secondary' : tokens.signal.caution.ink, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' }, color: tokens.ink.faint, mr: 0.75 }}>UPC</Box>
                  {variant.upc || variant.ean || '— missing —'}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: sharedMpn ? tokens.signal.caution.ink : 'text.secondary', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' }, color: tokens.ink.faint, mr: 0.75 }}>MPN</Box>
                  {variant.mpn || '—'}
                </Typography>
              </Box>
            ))}
          </Box>
          {warning && <NeutralNote>{warning}</NeutralNote>}
        </Box>
      )}

      {(linkedVariantsLoading || linkedVariants.length > 1) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
              Linked catalog variants
            </Typography>
            {linkedVariants.length > 1 && (
              <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{linkedVariants.length}</Typography>
            )}
          </Box>
          {linkedVariantsLoading && linkedVariants.length <= 1 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>Looking up linked configurations…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {linkedVariants.map((variantProduct) => {
                const isCurrent = variantProduct.id === product.id;
                const thumb = toAbsoluteImageUrl(variantProduct.images?.[0]);
                const model = variantProduct.model || variantProduct.sku || '';
                const sheet = isCurrent && product.pricingData
                  ? product.pricingData
                  : toListingPricingData(pricingQuery.data?.[`${variantProduct.id}::${model}`]);
                const listed = linkedCatalogListedPrice(
                  variantProduct,
                  sheet,
                  storePricesQuery.data?.[variantProduct.id],
                );
                const waitingOnStore = isStoreScoped && storePricesQuery.isLoading && listed.source !== 'store';
                const waitingOnMap = !isStoreScoped && pricingQuery.isLoading && listed.source === 'catalog';
                const amount = waitingOnStore || waitingOnMap ? null : listed.amount;
                const priceLabel = amount != null ? formatMoneyForProduct(variantProduct, amount) : null;
                return (
                  <Box
                    key={variantProduct.id}
                    role={isCurrent ? undefined : 'button'}
                    tabIndex={isCurrent ? undefined : 0}
                    onClick={() => !isCurrent && onLinkedProductClick(variantProduct)}
                    onKeyDown={(e) => {
                      if (!isCurrent && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onLinkedProductClick(variantProduct);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      p: '9px 11px',
                      borderRadius: '9px',
                      border: '1px solid',
                      borderColor: isCurrent ? tokens.accent.border : tokens.surface.tint,
                      bgcolor: isCurrent ? tokens.surface.subtle : 'transparent',
                      cursor: isCurrent ? 'default' : 'pointer',
                      '&:hover': isCurrent ? undefined : { bgcolor: tokens.surface.sunken, borderColor: 'divider' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      {thumb && (
                        <Box component="img" src={thumb} alt="" sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: '6px', bgcolor: tokens.surface.sunken, flexShrink: 0 }} />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {variantProduct.name || variantProduct.model}
                        </Typography>
                        <Typography sx={{ fontSize: '11.5px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>
                          {variantProduct.sku}{variantProduct.upc ? ` · UPC ${variantProduct.upc}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                        {linkedVariantKindLabel(variantProduct)}
                      </Typography>
                      {priceLabel && (
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>{priceLabel}</Typography>
                      )}
                      <Typography sx={{ fontSize: '11.5px', color: isCurrent ? 'primary.main' : tokens.ink.faint, fontWeight: isCurrent ? 600 : 400 }}>
                        {isCurrent ? 'viewing' : 'View'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
